import React, { useRef } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

import { Label } from '../ui/label';
import { Slider } from '../ui/slider';

// 2 MB raw file cap (per user's spec). Base64 inflates ~33% so ~2.7 MB on the wire.
const MAX_FILE_BYTES = 2 * 1024 * 1024;
const ACCEPT_LOGO = 'image/png,image/jpeg,image/jpg,image/svg+xml';
const ACCEPT_BG = 'image/png,image/jpeg,image/jpg';

const LOGO_POSITIONS = [
  { id: 'header-left',   label: 'Top-Left'    },
  { id: 'header-center', label: 'Top-Center'  },
  { id: 'header-right',  label: 'Top-Right'   },
  { id: 'footer-left',   label: 'Bot-Left'    },
  { id: 'footer-center', label: 'Bot-Center'  },
  { id: 'footer-right',  label: 'Bot-Right'   },
];

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result || ''));
    fr.onerror = () => reject(fr.error || new Error('read failed'));
    fr.readAsDataURL(file);
  });
}

/**
 * Shared branding controls for both the Open Carousel Designer and the
 * analysis-flow Carousel Generator dialog.
 *
 * Props (controlled):
 *   logoDataUrl: string|null       — current logo data URL
 *   onLogoChange: (url|null)=>void
 *   logoPosition: string           — one of 6 preset ids
 *   onLogoPositionChange: (id)=>void
 *   bgDataUrl: string|null         — current background image data URL
 *   onBgChange: (url|null)=>void
 *   bgOpacity: number              — 0..1
 *   onBgOpacityChange: (num)=>void
 *   testIdPrefix?: string          — prefix for data-testid attributes
 */
export function CarouselBrandingControls({
  logoDataUrl,
  onLogoChange,
  logoPosition = 'header-left',
  onLogoPositionChange,
  bgDataUrl,
  onBgChange,
  bgOpacity = 0.18,
  onBgOpacityChange,
  testIdPrefix = 'carousel-branding',
}) {
  const logoInputRef = useRef(null);
  const bgInputRef = useRef(null);

  const handlePickLogo = () => logoInputRef.current?.click();
  const handlePickBg = () => bgInputRef.current?.click();

  const handleLogoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      toast.error(`Logo is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 2 MB.`);
      return;
    }
    try {
      const url = await readAsDataURL(file);
      onLogoChange?.(url);
    } catch (err) {
      toast.error('Could not read the file. Try a different one.');
    }
  };

  const handleBgFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      toast.error(`Background is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 2 MB.`);
      return;
    }
    try {
      const url = await readAsDataURL(file);
      onBgChange?.(url);
    } catch (err) {
      toast.error('Could not read the file. Try a different one.');
    }
  };

  return (
    <div className="space-y-5" data-testid={`${testIdPrefix}-root`}>
      {/* ===== LOGO UPLOAD + POSITION ===== */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold">Brand logo</Label>
          {logoDataUrl && (
            <button
              type="button"
              onClick={() => onLogoChange?.(null)}
              className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-4 hover:underline inline-flex items-center gap-1"
              data-testid={`${testIdPrefix}-logo-clear`}
            >
              <X size={11} /> Clear
            </button>
          )}
        </div>

        {logoDataUrl ? (
          <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 p-2.5">
            <div
              className="h-14 w-14 rounded-md bg-card border border-border flex items-center justify-center overflow-hidden flex-shrink-0"
            >
              <img src={logoDataUrl} alt="logo preview" className="max-h-full max-w-full object-contain" />
            </div>
            <button
              type="button"
              onClick={handlePickLogo}
              className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
              data-testid={`${testIdPrefix}-logo-change`}
            >
              Replace…
            </button>
            <span className="text-[10px] text-muted-foreground ml-auto">Auto-resized in PDF</span>
          </div>
        ) : (
          <button
            type="button"
            onClick={handlePickLogo}
            className="w-full rounded-lg border-2 border-dashed border-border hover:border-foreground/40 hover:bg-muted/30 p-3.5 transition flex items-center gap-2 text-sm"
            data-testid={`${testIdPrefix}-logo-upload`}
          >
            <Upload size={14} className="text-muted-foreground" />
            <span className="text-muted-foreground">Upload logo (PNG/JPG/SVG, ≤ 2 MB)</span>
          </button>
        )}
        <input
          ref={logoInputRef}
          type="file"
          accept={ACCEPT_LOGO}
          onChange={handleLogoFile}
          className="hidden"
          data-testid={`${testIdPrefix}-logo-input`}
        />

        {/* 6-position picker */}
        <div className="space-y-1.5 pt-1">
          <Label className="text-[11px] font-medium text-muted-foreground tracking-wide">
            LOGO POSITION (DECK-WIDE)
          </Label>
          <div className="grid grid-cols-3 gap-1.5">
            {LOGO_POSITIONS.map((p) => {
              const active = p.id === logoPosition;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onLogoPositionChange?.(p.id)}
                  className={`relative rounded-md border text-[10px] font-semibold py-2 px-1.5 transition ${
                    active
                      ? 'bg-foreground text-background border-foreground shadow-sm'
                      : 'bg-card text-muted-foreground border-border hover:border-foreground/40 hover:text-foreground'
                  }`}
                  aria-pressed={active}
                  data-testid={`${testIdPrefix}-pos-${p.id}`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== BACKGROUND IMAGE + OPACITY ===== */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold">Background image</Label>
          {bgDataUrl && (
            <button
              type="button"
              onClick={() => onBgChange?.(null)}
              className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-4 hover:underline inline-flex items-center gap-1"
              data-testid={`${testIdPrefix}-bg-clear`}
            >
              <X size={11} /> Clear
            </button>
          )}
        </div>

        {bgDataUrl ? (
          <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 p-2.5">
            <div
              className="h-14 w-20 rounded-md bg-card border border-border flex items-center justify-center overflow-hidden flex-shrink-0"
            >
              <img src={bgDataUrl} alt="background preview" className="h-full w-full object-cover" />
            </div>
            <button
              type="button"
              onClick={handlePickBg}
              className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
              data-testid={`${testIdPrefix}-bg-change`}
            >
              Replace…
            </button>
            <span className="text-[10px] text-muted-foreground ml-auto">Skipped on cover</span>
          </div>
        ) : (
          <button
            type="button"
            onClick={handlePickBg}
            className="w-full rounded-lg border-2 border-dashed border-border hover:border-foreground/40 hover:bg-muted/30 p-3.5 transition flex items-center gap-2 text-sm"
            data-testid={`${testIdPrefix}-bg-upload`}
          >
            <ImageIcon size={14} className="text-muted-foreground" />
            <span className="text-muted-foreground">Upload background (PNG/JPG, ≤ 2 MB)</span>
          </button>
        )}
        <input
          ref={bgInputRef}
          type="file"
          accept={ACCEPT_BG}
          onChange={handleBgFile}
          className="hidden"
          data-testid={`${testIdPrefix}-bg-input`}
        />

        {/* Opacity slider — visible whenever a bg is set */}
        {bgDataUrl && (
          <div className="space-y-1.5 pt-1" data-testid={`${testIdPrefix}-opacity-wrap`}>
            <div className="flex items-center justify-between">
              <Label className="text-[11px] font-medium text-muted-foreground tracking-wide">
                BACKGROUND OPACITY
              </Label>
              <span className="text-[11px] font-mono text-foreground tabular-nums">
                {Math.round((bgOpacity ?? 0.18) * 100)}%
              </span>
            </div>
            <Slider
              value={[Math.round((bgOpacity ?? 0.18) * 100)]}
              onValueChange={(v) => onBgOpacityChange?.(Math.max(5, Math.min(100, v[0] ?? 18)) / 100)}
              min={5}
              max={100}
              step={1}
              data-testid={`${testIdPrefix}-opacity-slider`}
            />
            <p className="text-[10px] text-muted-foreground">
              Background applies to all slides except the cover.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CarouselBrandingControls;
